import React, { ReactNode, MouseEvent } from 'react';
import { mui } from '../tools/mui';
import { icons } from '../tools/icons';
import { jss } from '../tools/jss';
import Button from './Button';

interface TitleDialogProps {
  open?: boolean;
  scroll?: boolean;
  title?: string;
  buttonLabel?: string;
  disabled?: boolean;
  customButtons?: ReactNode;
  onClose?: () => void;
  onSubmit?: () => void;
  showCancel?: boolean;
  minContentWidth?: number;
  maxContentHeight?: number | string;
  children?: ReactNode;
}

const useStyles = jss.makeStyles((theme) => ({
  container: {
    background: '#fff',
  },
  title: {
    position: 'relative',
    fontSize: '2rem',
    fontWeight: 500,
  },
  content: {
    overflow: 'hidden',
    padding: '1.5rem',
  },
  actions: {
    position: 'relative',
    padding: '1rem 1.5rem',
  },
  closeButton: {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  submitButton: {
    marginLeft: '1rem',
  },
  buttonContainerLeft: {
    position: 'absolute',
    left: '1.5rem',
  },
  scrollable: {
    overflowY: 'auto',
    overflowX: 'hidden',
  },
}));

const TitleDialog: React.FC<TitleDialogProps> & { Divider: React.FC } = ({
  open = false,
  scroll = false,
  title = '',
  buttonLabel = 'Save',
  disabled = false,
  customButtons = null,
  onClose = () => {},
  onSubmit = () => {},
  showCancel = false,
  minContentWidth = 0,
  maxContentHeight = 'auto',
  children,
}) => {
  const styles = useStyles();

  const handleClickClose = (e: MouseEvent) => {
    e.preventDefault();
    onClose();
  };

  const handleClickSubmit = () => {
    onSubmit();
  };

  return (
    <mui.Dialog
      open={open}
      onClose={handleClickClose}
      PaperProps={{
        className: styles.container,
        style: { minWidth: minContentWidth },
      }}
    >
      <mui.DialogTitle className={styles.title}>
        <mui.Typography variant="h3">{title}</mui.Typography>
        <mui.IconButton
          aria-label="close"
          className={styles.closeButton}
          onClick={handleClickClose}
          data-testid="close-button"
          size="large"
        >
          <icons.Close />
        </mui.IconButton>
      </mui.DialogTitle>

      <mui.DialogContent
        dividers
        className={jss.combineStyles(styles.content, {
          [styles.scrollable]: scroll,
        })}
      >
        {children}
      </mui.DialogContent>

      <mui.DialogActions className={styles.actions}>
        <div className={styles.buttonContainerLeft}>{customButtons}</div>

        {showCancel && (
          <Button
            data-testid="cancel-category-dialog"
            onClick={handleClickClose}
            disableElevation
            color="primary"
            variant="text"
          >
            {/* TODO: get translations working with storybook */}
            Cancel
          </Button>
        )}

        <Button
          disabled={disabled}
          onClick={handleClickSubmit}
          variant="contained"
          color="primary"
          disableElevation
          className={styles.submitButton}
          data-testid="submit-button"
        >
          {buttonLabel}
        </Button>
      </mui.DialogActions>
    </mui.Dialog>
  );
};

TitleDialog.Divider = () => <mui.Divider style={{ margin: '0.5rem -1.5rem 1.5rem -1.5rem' }} />;

export default TitleDialog;
